import { ref, onMounted, onUnmounted } from 'vue'

export function useScrollReveal() {
  const revealElements = ref<HTMLElement[]>([])

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
        // Animate numbers
        const numbers = entry.target.querySelectorAll('[data-target]')
        numbers.forEach((num: Element) => {
          const el = num as HTMLElement
          const target = parseInt(el.getAttribute('data-target') || '0')
          if (!el.classList.contains('counted')) {
            animateNumber(el, target)
            el.classList.add('counted')
          }
        })
      }
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' })

  const animateNumber = (el: HTMLElement, target: number) => {
    let current = 0
    const inc = target / 60
    const timer = setInterval(() => {
      current += inc
      if (current >= target) {
        el.textContent = target.toLocaleString()
        clearInterval(timer)
      } else {
        el.textContent = Math.floor(current).toLocaleString()
      }
    }, 25)
  }

  const registerReveal = (el: HTMLElement | null) => {
    if (el) {
      revealElements.value.push(el)
      observer.observe(el)
    }
  }

  onUnmounted(() => observer.disconnect())

  return { registerReveal }
}

export function useParticles() {
  const particles = ref<Array<{ left: string; delay: string; duration: string }>>([])

  const generateParticles = (count: number = 30) => {
    particles.value = Array.from({ length: count }, () => ({
      left: Math.random() * 100 + '%',
      delay: Math.random() * 20 + 's',
      duration: (15 + Math.random() * 10) + 's'
    }))
  }

  return { particles, generateParticles }
}

export function useToast() {
  const toasts = ref<Array<{ id: number; message: string; type: string }>>([])
  let toastId = 0

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = ++toastId
    toasts.value.push({ id, message, type })
    setTimeout(() => {
      toasts.value = toasts.value.filter(t => t.id !== id)
    }, 3000)
  }

  return { toasts, showToast }
}

export function useNavbarScroll() {
  const isScrolled = ref(false)

  const handleScroll = () => {
    isScrolled.value = window.scrollY > 50
  }

  onMounted(() => window.addEventListener('scroll', handleScroll))
  onUnmounted(() => window.removeEventListener('scroll', handleScroll))

  return { isScrolled }
}

export function useParallax() {
  const offset = ref(0)

  const handleScroll = () => {
    offset.value = window.pageYOffset
  }

  onMounted(() => window.addEventListener('scroll', handleScroll))
  onUnmounted(() => window.removeEventListener('scroll', handleScroll))

  return { offset }
}

export function useLoading() {
  const isLoading = ref(true)

  const finishLoading = () => {
    setTimeout(() => { isLoading.value = false }, 2000)
  }

  return { isLoading, finishLoading }
}
